/**
 * GeneralErrorState Component
 *
 * A reusable error state component that displays a friendly internal server error message.
 * Includes an avatar image, title, and description, all localized using `react-i18next`.
 *
 * Props: None
 *
 * Behavior:
 * - Centers the content both vertically and horizontally.
 * - Displays an avatar image representing the error state.
 * - Uses translation keys `internal_server_error_title` and `internal_server_error_desc`.
 *
 * Usage:
 * ```tsx
 * <GeneralErrorState />
 * ```
 *
 * Notes:
 * - TailwindCSS is used for layout and styling.
 * - The avatar image path is derived from `BASE_LOCAL_IMAGES_DIR`.
 * - Can be extended to include retry buttons or additional error handling actions.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { BASE_LOCAL_IMAGES_DIR } from "../../utils/constants/CommonConstants";
import { internal_server_error_avatar_IMG } from "../../utils/constants/Images";

const GeneralErrorState: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <div className="flex flex-col gap-4">
        <img
          src={`${BASE_LOCAL_IMAGES_DIR}/${internal_server_error_avatar_IMG}`}
          alt="avatar"
          loading="lazy"
          className="mx-auto max-w-96"
        />
        <div className="flex flex-col gap-10">
          <div className="flex flex-col items-center gap-1">
            <h1 className="text-2xl font-bold text-headingTextColor">
              {t("internal_server_error_title")}
            </h1>
            <p className="w-4/5 text-center text-base font-normal text-textColor">
              {t("internal_server_error_desc")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralErrorState;
